import type { EChartsOption } from "echarts";

/**
 * ECharts seçenek üreticileri — SAF fonksiyonlar (birim testli). Kütüphaneden yalnızca TİP alınır
 * (`import type`), bu yüzden bu modül ana pakete ECharts kodu taşımaz.
 *
 * Para ve miktar hesapları `Decimal` / `Money` ile YAPILIR (bkz. `sales-values.ts`, `tank-values.ts`);
 * burada yalnızca ÇİZİM anında sayıya çevrilir — AGENTS.md §3.2'nin izin verdiği tek yer.
 */
export type ChartOption = EChartsOption;

const numberOrNull = (value: string | null | undefined): number | null =>
  value === null || value === undefined || value === "" ? null : Number(value);

const TR_DATE_TIME = new Intl.DateTimeFormat("tr-TR", {
  timeZone: "Europe/Istanbul",
  day: "2-digit",
  month: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});
const TR_DATE = new Intl.DateTimeFormat("tr-TR", {
  timeZone: "Europe/Istanbul",
  day: "2-digit",
  month: "2-digit",
});

/**
 * Grafik ızgarası: eksen etiketleri kırpılmasın diye kenar boşlukları sabittir.
 * Gösterge ÜSTTE durur (altta x ekseni etiketlerinin üstüne biniyordu); ızgara ona yer açar.
 *
 * `GRID` / `LEGEND_POSITION` / `VALUE_AXIS_LABEL` DIŞA AKTARILIR: vardiya raporlarının grafikleri de aynı
 * ızgarayı ve Türkiye biçimli eksen etiketini kullanır (tek görsel dil).
 */
export const GRID = { left: 64, right: 64, top: 64, bottom: 24, containLabel: true } as const;
export const LEGEND_POSITION = { top: 0, left: "center" } as const;

/** Eksen ve ipucu sayıları Türkiye biçimindedir (30.000 — AGENTS.md §6). Yalnızca ÇİZİM içindir; hesap değildir. */
const TR_NUMBER = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 2 });
export const formatChartNumber = (value: unknown): string =>
  typeof value === "number" && Number.isFinite(value) ? TR_NUMBER.format(value) : "—";
export const VALUE_AXIS_LABEL = { formatter: (value: number) => formatChartNumber(value) } as const;

export interface TankSeriesChartPoint {
  at: string;
  grossVolumeL: string;
  netVolumeL: string | null;
  temperatureC: string | null;
}

export interface TankSeriesChartLabels {
  gross: string;
  net: string;
  temperature: string;
  volumeAxis: string;
  temperatureAxis: string;
  safeFill: string;
  minLevel: string;
}

export interface TankSeriesChartInput {
  points: readonly TankSeriesChartPoint[];
  /** Ürün rengi (ör. motorin siyahı); yoksa tema paleti kullanılır. */
  productColor: string | null;
  safeFillVolumeL: string | null;
  minLevelL: string | null;
  /** Günlük çözünürlükte eksen etiketi saat değil TARİH gösterir. */
  daily?: boolean;
  labels: TankSeriesChartLabels;
}

/**
 * Tank seviye grafiği: brüt hacim + (varsa) net hacim (15 °C) birinci eksende, sıcaklık ikinci eksende.
 * Güvenli üst sınır ve minimum seviye yatay işaret çizgisiyle gösterilir. Dolumlar ani artış olarak
 * kendiliğinden görünür (ayrı seri gerekmez).
 */
export function tankSeriesChartOption(input: TankSeriesChartInput): ChartOption {
  const { labels, points } = input;
  const hasNet = points.some((point) => point.netVolumeL !== null);
  const hasTemperature = points.some((point) => point.temperatureC !== null);
  const formatAxis = (value: number): string =>
    input.daily ? TR_DATE.format(value) : TR_DATE_TIME.format(value);

  const markLineData = [
    input.safeFillVolumeL === null ? null : { yAxis: Number(input.safeFillVolumeL), name: labels.safeFill },
    input.minLevelL === null ? null : { yAxis: Number(input.minLevelL), name: labels.minLevel },
  ].filter((entry): entry is { yAxis: number; name: string } => entry !== null);

  const series: NonNullable<ChartOption["series"]> = [
    {
      type: "line",
      name: labels.gross,
      showSymbol: false,
      yAxisIndex: 0,
      ...(input.productColor ? { itemStyle: { color: input.productColor } } : {}),
      data: points.map((point) => [point.at, numberOrNull(point.grossVolumeL)]),
      ...(markLineData.length > 0
        ? {
            markLine: {
              silent: true,
              symbol: "none",
              label: { formatter: "{b}", position: "insideEndTop" },
              lineStyle: { type: "dashed" as const },
              data: markLineData,
            },
          }
        : {}),
    },
  ];
  if (hasNet)
    series.push({
      type: "line",
      name: labels.net,
      showSymbol: false,
      yAxisIndex: 0,
      lineStyle: { type: "dashed" },
      ...(input.productColor ? { itemStyle: { color: input.productColor } } : {}),
      data: points.map((point) => [point.at, numberOrNull(point.netVolumeL)]),
    });
  if (hasTemperature)
    series.push({
      type: "line",
      name: labels.temperature,
      showSymbol: false,
      yAxisIndex: 1,
      data: points.map((point) => [point.at, numberOrNull(point.temperatureC)]),
    });

  return {
    grid: GRID,
    tooltip: { trigger: "axis", valueFormatter: formatChartNumber },
    legend: {
      ...LEGEND_POSITION,
      data: series.map((entry) => String((entry as { name?: string }).name ?? "")),
    },
    xAxis: { type: "time", axisLabel: { hideOverlap: true, formatter: formatAxis } },
    yAxis: [
      { type: "value", name: labels.volumeAxis, position: "left", axisLabel: VALUE_AXIS_LABEL },
      {
        type: "value",
        name: labels.temperatureAxis,
        position: "right",
        splitLine: { show: false },
        axisLabel: VALUE_AXIS_LABEL,
      },
    ],
    series,
  };
}

export interface CategorySeries {
  name: string;
  /** Ürün rengi; yoksa tema paleti kullanılır. */
  color: string | null;
  /** `categories` ile aynı uzunlukta ondalık DİZGE listesi. */
  values: readonly string[];
}

/** Ürün bazında yığılmış sütun grafiği (günlük satış litresi). */
export function stackedLitresChartOption(
  categories: readonly string[],
  series: readonly CategorySeries[],
  labels: { litresAxis: string },
): ChartOption {
  return {
    grid: GRID,
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: { ...LEGEND_POSITION, data: series.map((entry) => entry.name) },
    xAxis: { type: "category", data: [...categories], axisLabel: { hideOverlap: true } },
    yAxis: { type: "value", name: labels.litresAxis, axisLabel: VALUE_AXIS_LABEL },
    series: series.map((entry) => ({
      type: "bar",
      name: entry.name,
      stack: "litres",
      ...(entry.color ? { itemStyle: { color: entry.color } } : {}),
      data: entry.values.map((value) => numberOrNull(value)),
    })),
  };
}

export interface CumulativeVarianceChartInput {
  /** `YYYY-MM-DD` iş günleri; eksen etiketi TR biçimine çevrilir. */
  categories: readonly string[];
  dailyValues: readonly string[];
  cumulativeValues: readonly string[];
}

export interface CumulativeVarianceChartLabels {
  daily: string;
  cumulative: string;
  litresAxis: string;
}

/**
 * Islak stok mutabakatı: günlük fark SÜTUN, kümülatif fark ÇİZGİ — ikisi de litre olduğu için TEK eksen
 * kullanılır (iki eksen aynı birimde yanıltıcı olurdu). Sıfır çizgisi belirgindir: kümülatif çizginin
 * sıfırın altına yerleşip orada kalması sızıntı / kaçak işaretidir.
 */
export function cumulativeVarianceChartOption(
  input: CumulativeVarianceChartInput,
  labels: CumulativeVarianceChartLabels,
): ChartOption {
  const formatDay = (value: string): string => {
    const parsed = Date.parse(`${value}T00:00:00Z`);
    return Number.isNaN(parsed) ? value : TR_DATE.format(parsed);
  };
  return {
    grid: GRID,
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" }, valueFormatter: formatChartNumber },
    legend: { ...LEGEND_POSITION, data: [labels.daily, labels.cumulative] },
    xAxis: {
      type: "category",
      data: input.categories.map((category) => formatDay(category)),
      axisLabel: { hideOverlap: true },
    },
    yAxis: { type: "value", name: labels.litresAxis, axisLabel: VALUE_AXIS_LABEL },
    series: [
      {
        type: "bar",
        name: labels.daily,
        data: input.dailyValues.map((value) => numberOrNull(value)),
      },
      {
        type: "line",
        name: labels.cumulative,
        symbol: "circle",
        symbolSize: 5,
        data: input.cumulativeValues.map((value) => numberOrNull(value)),
        markLine: {
          silent: true,
          symbol: "none",
          lineStyle: { type: "solid" as const },
          data: [{ yAxis: 0 }],
        },
      },
    ],
  };
}

/** Saatlik satış: ürün başına bir çizgi (saat × litre). */
export function hourlyLitresChartOption(
  categories: readonly string[],
  series: readonly CategorySeries[],
  labels: { litresAxis: string },
): ChartOption {
  return {
    grid: GRID,
    tooltip: { trigger: "axis", valueFormatter: formatChartNumber },
    legend: { ...LEGEND_POSITION, data: series.map((entry) => entry.name) },
    xAxis: { type: "category", data: [...categories], axisLabel: { hideOverlap: true } },
    yAxis: { type: "value", name: labels.litresAxis, axisLabel: VALUE_AXIS_LABEL },
    series: series.map((entry) => ({
      type: "line",
      name: entry.name,
      showSymbol: false,
      ...(entry.color ? { itemStyle: { color: entry.color } } : {}),
      data: entry.values.map((value) => numberOrNull(value)),
    })),
  };
}
